import { ChevronDownIcon, ChevronsUpDownIcon, ChevronUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Rating } from '@/components/atoms/Rating';
import { ReplayButton } from '@/components/atoms/ReplayButton/ReplayButton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { timeFromNowShort } from '@/util/dateUtils';

import { formatDateRangeLabel } from '../../utils/formatDateRangeLabel';

export type UserCommentsSortKey = 'csat' | 'date';
type SortDirection = 'asc' | 'desc';

export interface UserCommentRow {
  id: string;
  sessionId: string;
  user: string;
  comment: string;
  csat: number;
  createdAt: string;
  brainParentId?: string;
  sessionStart?: string;
}

export interface UserCommentsTableProps {
  rows: UserCommentRow[];
  total: number;
  startDate: string;
  endDate: string;
  sort: { key: UserCommentsSortKey; direction: SortDirection };
  onSortChange: (key: UserCommentsSortKey) => void;
  pageIndex: number;
  pageSize?: number;
  onPageChange: (pageIndex: number) => void;
  onOpenConversation: (row: UserCommentRow) => void;
  isLoading?: boolean;
}

const DEFAULT_PAGE_SIZE = 10;
const SKELETON_ROW_KEYS = ['a', 'b', 'c', 'd', 'e'];

const SortIndicator = ({ active, direction }: { active: boolean; direction: SortDirection }) => {
  if (!active) {
    return <ChevronsUpDownIcon className="size-3.5 text-muted-foreground" />;
  }
  const DirectionIcon = direction === 'asc' ? ChevronUpIcon : ChevronDownIcon;
  return <DirectionIcon className="size-3.5 text-foreground" />;
};

const SortableHead = ({
  column,
  label,
  sort,
  onSort,
}: {
  column: UserCommentsSortKey;
  label: string;
  sort: UserCommentsTableProps['sort'];
  onSort: (column: UserCommentsSortKey) => void;
}) => {
  const active = sort.key === column;
  let ariaSort: 'ascending' | 'descending' | 'none' = 'none';
  if (active) {
    ariaSort = sort.direction === 'asc' ? 'ascending' : 'descending';
  }
  return (
    <TableHead aria-sort={ariaSort} className="h-12 w-32 px-4 py-1">
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 text-sm leading-6 font-medium text-muted-foreground hover:text-foreground"
      >
        {label}
        <SortIndicator active={active} direction={sort.direction} />
      </button>
    </TableHead>
  );
};

const headClass = 'h-12 px-4 py-1 text-sm leading-6 font-medium text-muted-foreground';
const cellClass = 'h-14 px-4 text-sm leading-5 font-normal text-foreground';

export const UserCommentsTable = ({
  rows,
  total,
  startDate,
  endDate,
  sort,
  onSortChange,
  pageIndex,
  pageSize = DEFAULT_PAGE_SIZE,
  onPageChange,
  onOpenConversation,
  isLoading,
}: UserCommentsTableProps) => {
  const { t } = useTranslation();

  return (
    <Card className="rounded-lg bg-background text-foreground shadow-none">
      <CardHeader>
        <div className="flex flex-col gap-2">
          <CardTitle>{t('analytics.user_comments.title')}</CardTitle>
          <CardDescription>{formatDateRangeLabel(startDate, endDate)}</CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <Table containerClassName="rounded-lg border border-border overflow-hidden">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-12 w-16 px-4 py-1" />
              <TableHead className={`${headClass} w-40`}>{t('analytics.fields.user')}</TableHead>
              <TableHead className={headClass}>
                {t('analytics.user_comments.column_comment')}
              </TableHead>
              <SortableHead
                column="csat"
                label={t('analytics.fields.csat')}
                sort={sort}
                onSort={onSortChange}
              />
              <SortableHead
                column="date"
                label={t('analytics.user_comments.column_date')}
                sort={sort}
                onSort={onSortChange}
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? SKELETON_ROW_KEYS.map((key) => (
                  <TableRow key={key} className="h-14">
                    <TableCell className={cellClass}>
                      <Skeleton className="size-8" />
                    </TableCell>
                    <TableCell className={cellClass}>
                      <Skeleton className="h-5 w-24" />
                    </TableCell>
                    <TableCell className={cellClass}>
                      <Skeleton className="h-5 w-full" />
                    </TableCell>
                    <TableCell className={cellClass}>
                      <Skeleton className="h-5 w-28" />
                    </TableCell>
                    <TableCell className={cellClass}>
                      <Skeleton className="h-5 w-16" />
                    </TableCell>
                  </TableRow>
                ))
              : rows.map((row) => (
                  <TableRow key={row.id} className="h-14">
                    <TableCell className={cellClass}>
                      <ReplayButton
                        sessionId={row.sessionId}
                        onClick={() => onOpenConversation(row)}
                      />
                    </TableCell>
                    <TableCell className={`${cellClass} w-40 max-w-40 truncate`} title={row.user}>
                      {row.user}
                    </TableCell>
                    <TableCell className={`${cellClass} max-w-0 truncate`} title={row.comment}>
                      {row.comment}
                    </TableCell>
                    <TableCell className={cellClass}>
                      <div className="flex items-center gap-2">
                        <Rating
                          stars={row.csat}
                          className="[&_svg]:fill-primary [&_svg]:text-primary"
                        />
                        <span className="tabular-nums">{row.csat}</span>
                      </div>
                    </TableCell>
                    <TableCell className={`${cellClass} whitespace-nowrap`}>
                      {timeFromNowShort(row.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        <div className="mt-4 flex h-10 items-center justify-between pl-1">
          <span className="text-sm text-muted-foreground">
            {t('analytics.user_comments.count', { count: total })}
          </span>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline-neutral"
              size="sm"
              onClick={() => onPageChange(pageIndex - 1)}
              disabled={pageIndex <= 0}
            >
              {t('table.previous')}
            </Button>
            <Button
              variant="outline-neutral"
              size="sm"
              onClick={() => onPageChange(pageIndex + 1)}
              disabled={(pageIndex + 1) * pageSize >= total}
            >
              {t('table.next')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
